import { logger } from './logger';

export const CHUNK_RELOAD_STORAGE_KEY = 'spirit_vite_chunk_reloaded_at';
const CHUNK_RELOAD_COOLDOWN_MS = 10000; // 10 seconds cooldown to prevent reload loops

/**
 * Checks whether an error is caused by a missing Vite chunk after a deployment
 */
export const isChunkLoadError = (error: unknown): boolean => {
    if (!error) return false;
    const message = error instanceof Error ? error.message : String(error);
    return (
        message.includes('Failed to fetch dynamically imported module') ||
        message.includes('Importing a module script failed') ||
        message.includes('error loading dynamically imported module') ||
        message.includes('Unable to preload CSS')
    );
};

/**
 * Attempts a silent reload with cooldown protection
 * Returns true if reload was initiated, false if blocked by cooldown or environment
 */
export const triggerChunkReload = (reason: string = 'chunk_reload'): boolean => {
    try {
        if (typeof window === 'undefined' || !window.sessionStorage) return false;

        const lastReloadStr = window.sessionStorage.getItem(CHUNK_RELOAD_STORAGE_KEY);
        const now = Date.now();

        if (lastReloadStr) {
            const lastReload = parseInt(lastReloadStr, 10);
            if (!isNaN(lastReload) && now - lastReload < CHUNK_RELOAD_COOLDOWN_MS) {
                logger.warn(`Chunk reload blocked by cooldown latch (${reason})`, {
                    elapsedMs: now - lastReload,
                    cooldownMs: CHUNK_RELOAD_COOLDOWN_MS
                });
                return false;
            }
        }

        window.sessionStorage.setItem(CHUNK_RELOAD_STORAGE_KEY, String(now));
        logger.warn(`Reloading application due to chunk load failure (${reason})`);
        window.location.reload();
        return true;
    } catch (e) {
        logger.error('Failed to trigger chunk reload latch', e);
        return false;
    }
};

/**
 * Setup global error handlers
 * Catches errors that slip through try/catch blocks
 */
export const setupGlobalErrorHandling = (): void => {
    // 1. Catch Vite dynamic import chunk failures
    window.addEventListener('vite:preloadError', (event) => {
        logger.warn('vite:preloadError event caught, initiating silent reload', { event });
        triggerChunkReload('vite:preloadError');
    });

    // 2. Catch unhandled JavaScript errors
    window.addEventListener('error', (event) => {
        const error = event.error || new Error(event.message);
        if (isChunkLoadError(error)) {
            const reloaded = triggerChunkReload('window_error_chunk');
            if (reloaded) return;
        }

        logger.error('Unhandled JavaScript error', error, {
            type: 'unhandled_error',
            filename: event.filename,
            lineno: event.lineno,
            colno: event.colno,
        });
    });

    // 3. Catch unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
        const error =
            event.reason instanceof Error
                ? event.reason
                : new Error(String(event.reason));

        if (isChunkLoadError(error)) {
            const reloaded = triggerChunkReload('unhandledrejection_chunk');
            if (reloaded) return;
        }

        logger.error('Unhandled promise rejection', error, { type: 'unhandled_rejection' });
    });

    logger.info('Global error handlers initialized');
};


